/**
 * Google Drive Authentication Component
 * Handles sign-in/sign-out and displays authentication status
 */

import React, { useState, useEffect } from 'react';
import { googleDriveService } from '../../services/GoogleDriveService';
import { GoogleDriveAuth as AuthState } from '../../types/persistence';

interface GoogleDriveAuthProps {
  onAuthChange?: (isSignedIn: boolean) => void;
  className?: string;
}

export const GoogleDriveAuth: React.FC<GoogleDriveAuthProps> = ({
  onAuthChange,
  className = ''
}) => {
  const [authState, setAuthState] = useState<AuthState>({ isSignedIn: false });
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    // Check initial auth state
    const checkAuthState = () => {
      const currentState = googleDriveService.getAuthState();
      setAuthState(currentState);
      onAuthChange?.(currentState.isSignedIn);
    };

    checkAuthState();

    // Set up periodic check for auth state changes
    const interval = setInterval(checkAuthState, 5000);

    return () => clearInterval(interval);
  }, [onAuthChange]);

  const handleSignIn = async () => {
    setIsLoading(true);
    setError(null);

    try {
      // Ensure the service is initialized
      await googleDriveService.ensureInitialized();

      const success = await googleDriveService.signIn();
      if (success) {
        const newState = googleDriveService.getAuthState();
        setAuthState(newState);
        onAuthChange?.(newState.isSignedIn);
      } else {
        setError('Sign-in was cancelled or failed');
      }
    } catch (err) {
      console.error('Sign-in error:', err);
      setError(err instanceof Error ? err.message : 'Sign-in failed');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSignOut = async () => {
    setIsLoading(true);
    setError(null);

    try {
      await googleDriveService.signOut();
      setAuthState({ isSignedIn: false });
      onAuthChange?.(false);
    } catch (err) {
      console.error('Sign-out error:', err);
      setError(err instanceof Error ? err.message : 'Sign-out failed');
    } finally {
      setIsLoading(false);
    }
  };

  const isTokenExpired = () => {
    if (!authState.expiresAt) return false;
    return new Date() >= authState.expiresAt;
  };

  const getTimeUntilExpiry = () => {
    if (!authState.expiresAt) return null;
    const now = new Date();
    const expiry = authState.expiresAt;
    const diffMs = expiry.getTime() - now.getTime();
    const diffMins = Math.floor(diffMs / 60000);

    if (diffMins < 1) return 'Expires soon';
    if (diffMins < 60) return `Expires in ${diffMins}m`;

    const diffHours = Math.floor(diffMins / 60);
    if (diffHours < 24) return `Expires in ${diffHours}h`;

    return 'Valid';
  };

  if (!authState.isSignedIn) {
    return (
      <div className={`flex flex-col space-y-2 ${className}`}>
        <div className="flex items-center space-x-2">
          <svg className="w-5 h-5 text-ink-faint" fill="currentColor" viewBox="0 0 20 20">
            <path d="M3 4a1 1 0 011-1h12a1 1 0 011 1v2a1 1 0 01-1 1H4a1 1 0 01-1-1V4zM3 10a1 1 0 011-1h6a1 1 0 011 1v6a1 1 0 01-1 1H4a1 1 0 01-1-1v-6zM14 9a1 1 0 00-1 1v6a1 1 0 001 1h2a1 1 0 001-1v-6a1 1 0 00-1-1h-2z" />
          </svg>
          <span className="text-sm text-ink-dim">Not connected to Google Drive</span>
        </div>

        <button
          onClick={handleSignIn}
          disabled={isLoading}
          className="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-opacity-50 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {isLoading ? (
            <>
              <svg className="w-4 h-4 mr-2 animate-spin" fill="none" viewBox="0 0 24 24">
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                ></circle>
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="m4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                ></path>
              </svg>
              Connecting...
            </>
          ) : (
            <>
              <svg className="w-4 h-4 mr-2" fill="currentColor" viewBox="0 0 20 20">
                <path d="M3 4a1 1 0 011-1h12a1 1 0 011 1v2a1 1 0 01-1 1H4a1 1 0 01-1-1V4zM3 10a1 1 0 011-1h6a1 1 0 011 1v6a1 1 0 01-1 1H4a1 1 0 01-1-1v-6zM14 9a1 1 0 00-1 1v6a1 1 0 001 1h2a1 1 0 001-1v-6a1 1 0 00-1-1h-2z" />
              </svg>
              Connect to Google Drive
            </>
          )}
        </button>

        {error && (
          <div className="text-sm text-red-600 bg-red-50 dark:bg-red-900/30 border border-red-200 rounded-md p-2">
            {error}
          </div>
        )}
      </div>
    );
  }

  return (
    <div className={`flex flex-col space-y-2 ${className}`}>
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-2">
          <svg className="w-5 h-5 text-green-500" fill="currentColor" viewBox="0 0 20 20">
            <path
              fillRule="evenodd"
              d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
              clipRule="evenodd"
            />
          </svg>
          <span className="text-sm text-green-600 font-medium">Connected to Google Drive</span>
        </div>

        <button
          onClick={handleSignOut}
          disabled={isLoading}
          className="text-sm text-ink-dim hover:text-ink focus:outline-none focus:ring-2 focus:ring-line-strong focus:ring-opacity-50 rounded disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {isLoading ? 'Disconnecting...' : 'Disconnect'}
        </button>
      </div>

      {authState.expiresAt && (
        <div className="text-xs text-ink-faint">
          {isTokenExpired() ? (
            <span className="text-yellow-600">Token expired - please reconnect</span>
          ) : (
            <span>{getTimeUntilExpiry()}</span>
          )}
        </div>
      )}

      {error && (
        <div className="text-sm text-red-600 bg-red-50 dark:bg-red-900/30 border border-red-200 rounded-md p-2">
          {error}
        </div>
      )}
    </div>
  );
};

export default GoogleDriveAuth;
